import { Badge, Tooltip, Stack, Typography } from '@mui/material'
import { styled as muiStyled } from '@mui/material/styles'
import I18n from 'i18n-js'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { makeStyles } from 'tss-react/mui'

import Analytics from '@src/analytics/Analytics'

import fonts from '../../../../lib/Font'
import { formatWithoutCents } from '../../../../lib/Utils'
import PlatformAvatarGroup from '../../../reporting/PlatformAvatarGroup'
import LegacySvg from '../../../shared/LegacySvg'

const Wrapper = muiStyled('div')(({ theme, isFeatured }) => ({
  boxSizing: 'border-box',
  width: '100%',
  background: isFeatured ? theme.palette.premiumAds.main : undefined,
  padding: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderTop: `1px solid ${theme.palette.grey[200]}`,
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  fontFamily: fonts.family.lato,
}))

const Channels = styled.div`
  display: flex;
`

const CreditAmount = muiStyled('div')(({ theme, paidType, type }) => ({
  fontSize: '16px',
  lineHeight: '19px',
  padding: '2px 8px',
  fontWeight: 'bold',
  background:
    type === 'paidAd'
      ? paidType === 'retailer_funded'
        ? theme.palette.secondary.main
        : theme.palette.premiumAds.main
      : theme.palette.secondary.main,
  color:
    type === 'paidAd'
      ? paidType === 'retailer_funded'
        ? theme.palette.secondary.contrastText
        : theme.palette.grey[800]
      : theme.palette.secondary.contrastText,
  display: type === 'paidAd' || type === 'localAd' ? 'block' : 'none',
}))

const Featured = muiStyled('div')(({ theme }) => ({
  fontSize: '0.9em',
  color: theme.palette.common.black,
}))

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  width: 24px;
  border-radius: 50%;
  overflow: hidden;
  margin: 0 4px;
`

const useStyles = makeStyles()((theme) => ({
  badge: {
    marginRight: theme.spacing(1.25),
  },
}))

const formatAmount = (amount) => formatWithoutCents.format(amount / 100)
const formatChannelEvent = (type = '') => {
  let firstLetter = type.slice(0, 1)
  let channel = firstLetter.toUpperCase() + type.slice(1)
  return `${channel} Share Icon Clicked`
}

const platformData = {
  'facebook': 'facebook',
  'instagram': 'instagram',
  'messenger': 'fb-messenger',
  'audience network': 'fb-audience',
}

const renderAdPlatforms = (platforms = [], isFeatured) => {
  const orderedPlatforms = Object.keys(platformData)
  platforms.sort((a, b) => {
    return orderedPlatforms.indexOf(a) - orderedPlatforms.indexOf(b)
  })

  return (
    <PlatformAvatarGroup
      borderColor={isFeatured ? 'premiumAds.main' : null}
      platforms={platforms}
    />
  )
}

const renderChannels = (channels, campaignId, viewOrActivate, style) =>
  channels.map((channel, i) => {
    const key = `${channel.type}-${channel.slug}-${i}`
    if (!channel.to) {
      return (
        <IconWrapper key={key}>
          <LegacySvg height="36px" symbol={channel.type} width="36px" />
        </IconWrapper>
      )
    }
    return (
      <Link
        key={key}
        onClick={(event) => {
          Analytics.track('Click', {
            campaignId,
            name: formatChannelEvent(channel.type),
            type: channel.type,
          }).finally(() => {
            viewOrActivate(event, channel.to)
          })
        }}
      >
        <Tooltip
          title={I18n.t('content.channelIconBadge', {
            count: channel.count,
            type: channel.type.charAt(0).toUpperCase() + channel.type.slice(1),
          })}
        >
          <Badge
            badgeContent={channel.count}
            color="secondary"
            max={9}
            className={style}
          >
            <IconWrapper>
              <LegacySvg height="36px" symbol={channel.type} width="36px" />
            </IconWrapper>
          </Badge>
        </Tooltip>
      </Link>
    )
  })

const CampaignInfo = (props) => {
  const { classes } = useStyles()

  if (props.channels.length) {
    return (
      <Wrapper isFeatured={props.isFeatured}>
        <Channels>
          {props.type === 'paidAd'
            ? renderAdPlatforms(props.platforms, props.isFeatured)
            : renderChannels(
                props.channels,
                props.id,
                props.viewOrActivate,
                classes.badge,
              )}
        </Channels>
        {props.isFeatured && (
          <Featured>
            {I18n.t('content.featuredPremiumAd').toUpperCase()}
          </Featured>
        )}
        {props.type !== 'campaign' && (
          <Stack direction="row" alignItems="center" spacing={2}>
            {props.type === 'paidAd' &&
              !props.isActivated &&
              !props.startDate < Date.now() && (
                <Typography variant="subtitle">
                  {props.paidType === 'retailer_funded'
                    ? I18n.t('content.minimumSpend')
                    : I18n.t('content.claimableAdFunds')}
                </Typography>
              )}
            <CreditAmount type={props.type} paidType={props.paidType}>
              {formatAmount(props.creditAmount)}
            </CreditAmount>
          </Stack>
        )}
      </Wrapper>
    )
  } else {
    return null
  }
}

CampaignInfo.propTypes = {
  //TODO: Hook these up in future ticket
  // channelActivated: PropTypes.shape({
  //   facebook: PropTypes.bool,
  //   instagram: PropTypes.bool,
  // }),
  // channelOptions: PropTypes.shape({
  //   facebook: PropTypes.number,
  //   instagram: PropTypes.number,
  // }),
  channels: PropTypes.array,
  creditAmount: PropTypes.number,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  platforms: PropTypes.array,
  type: PropTypes.oneOf(['paidAd', 'localAd', 'campaign', 'contentDiscovery']),
  viewOrActivate: PropTypes.func,
}

CampaignInfo.defaultProps = {
  channels: ['facebook', 'instagram'],
}

export default CampaignInfo
